// ============================================================================
// BESPOKIAN Health - Pharmacist Cockpit Fill Page
// Prescription fill workflow with FEFO lot selection and NDC verification
// ============================================================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { 
  ArrowLeft, Package, Printer, CheckCircle2, 
  Barcode, Calendar, AlertTriangle, Pill, Scale
} from 'lucide-react';
import { 
  Card, CardHeader, CardTitle, CardContent, 
  Button, Alert, Badge, Spinner, Input
} from '@/components/common/ui';
import { ControlledSubstanceBadge, ControlledSubstanceWarning } from '@/components/common/ControlledSubstanceBadge';
import { queueApi } from '@/api/queue.api';
import { formatCurrency } from '@/types';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export function FillPage() {
  const { id } = useParams({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [fillData, setFillData] = useState({
    lotNumber: '',
    expirationDate: '',
    ndcScanned: '',
    quantityFilled: 0,
    doubleCountVerified: false,
  });

  // Fetch prescription with fill data
  const { data: rx, isLoading } = useQuery({
    queryKey: ['prescription', id],
    queryFn: () => queueApi.getPrescription(id as string),
    enabled: !!id,
  });

  // Fetch available inventory lots
  const { data: lots, isLoading: lotsLoading } = useQuery({
    queryKey: ['inventory', rx?.ndc],
    queryFn: () => queueApi.getAvailableLots(rx?.ndc || ''),
    enabled: !!rx?.ndc,
  });

  // Fill mutation
  const fillMutation = useMutation({
    mutationFn: () => queueApi.fillPrescription(id as string, {
      lotNumber: fillData.lotNumber,
      expirationDate: fillData.expirationDate,
      ndcScanned: fillData.ndcScanned,
      quantityFilled: fillData.quantityFilled,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queue'] });
      queryClient.invalidateQueries({ queryKey: ['prescription', id] });
      toast.success('Prescription filled successfully');
      navigate({ to: '/queue' });
    },
    onError: (error: Error) => {
      toast.error(`Fill failed: ${error.message}`);
    },
  });

  // Print label mutation
  const printLabelMutation = useMutation({
    mutationFn: () => queueApi.printLabel(id as string),
    onSuccess: () => {
      toast.success('Label sent to printer');
    },
    onError: () => {
      toast.error('Failed to print label');
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!rx) {
    return <Alert variant="danger">Prescription not found</Alert>;
  }

  // Verify NDC matches
  const ndcMatches = fillData.ndcScanned === rx.ndc || fillData.ndcScanned === '';
  
  // Check if C-II requires double count
  const requiresDoubleCount = rx.deaSchedule === 'CII';

  // Check if ready to fill
  const canFill = 
    fillData.lotNumber && 
    fillData.expirationDate && 
    fillData.quantityFilled === rx.quantity &&
    ndcMatches &&
    (!requiresDoubleCount || fillData.doubleCountVerified);

  // Sort lots by expiration (FEFO - First Expiry First Out)
  const sortedLots = lots?.sort((a: any, b: any) => 
    new Date(a.expirationDate).getTime() - new Date(b.expirationDate).getTime()
  ) || [];

  const handleSelectLot = (lot: any) => {
    setFillData({
      ...fillData,
      lotNumber: lot.lotNumber,
      expirationDate: format(new Date(lot.expirationDate), 'yyyy-MM-dd'),
    });
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate({ to: '/queue' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Queue
        </Button>
        <div className="flex items-center gap-2">
          <Badge variant="primary">Ready to Fill</Badge>
          {rx.deaSchedule && (
            <ControlledSubstanceBadge schedule={rx.deaSchedule} />
          )}
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">Fill Rx #{rx.rxNumber}</h1>
        <p className="text-slate-500">
          {rx.drugName} {rx.strength} • Qty: {rx.quantity} • {rx.patientName}
        </p>
      </div>

      {/* C-II Warning */}
      {requiresDoubleCount && <ControlledSubstanceWarning schedule="CII" />}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Prescription Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pill className="h-5 w-5" />
              Prescription Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-500">Patient</span>
              <span className="font-medium">{rx.patientName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Drug</span>
              <span className="font-medium">{rx.drugName} {rx.strength}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">NDC</span>
              <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{rx.ndc}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Quantity</span>
              <span className="font-medium">{rx.quantity}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Days Supply</span>
              <span className="font-medium">{rx.daysSupply}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Prescriber</span>
              <span className="font-medium">Dr. {rx.prescriberName}</span>
            </div>
            
            {/* SIG Box */}
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mt-4">
              <div className="font-semibold text-amber-900 mb-1 flex items-center gap-2">
                <Scale className="h-4 w-4" />
                Sig (Directions)
              </div>
              <div className="font-mono text-sm text-amber-800">{rx.sig}</div>
            </div>
          </CardContent>
        </Card>

        {/* Fill Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Fill Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* NDC Scan */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Barcode className="h-4 w-4 inline mr-1" />
                Scan NDC Barcode
              </label>
              <Input
                placeholder="Scan or enter NDC..."
                value={fillData.ndcScanned}
                onChange={(e) => setFillData({ ...fillData, ndcScanned: e.target.value })}
              />
              {!ndcMatches && fillData.ndcScanned && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  NDC MISMATCH! Expected: {rx.ndc}
                </p>
              )}
              {ndcMatches && fillData.ndcScanned && (
                <p className="text-emerald-600 text-sm mt-1 flex items-center gap-1">
                  <CheckCircle2 className="h-4 w-4" />
                  NDC verified
                </p>
              )}
            </div>

            {/* Lot Number */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Lot Number
              </label>
              <Input
                placeholder="Enter or select lot number..."
                value={fillData.lotNumber}
                onChange={(e) => setFillData({ ...fillData, lotNumber: e.target.value })}
              />
            </div>

            {/* Expiration Date */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                <Calendar className="h-4 w-4 inline mr-1" />
                Expiration Date
              </label>
              <Input
                type="date"
                value={fillData.expirationDate}
                onChange={(e) => setFillData({ ...fillData, expirationDate: e.target.value })}
                min={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>

            {/* Quantity Filled */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Quantity Filled
              </label>
              <Input
                type="number"
                value={fillData.quantityFilled || ''}
                onChange={(e) => setFillData({ ...fillData, quantityFilled: Number(e.target.value) })}
                min={1}
                max={rx.quantity}
              />
              {fillData.quantityFilled > 0 && fillData.quantityFilled !== rx.quantity && (
                <p className="text-amber-600 text-sm mt-1 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Quantity differs from prescribed ({rx.quantity})
                </p>
              )}
            </div>

            {/* Double Count for C-II */}
            {requiresDoubleCount && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fillData.doubleCountVerified}
                    onChange={(e) => setFillData({ ...fillData, doubleCountVerified: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-red-300 text-red-600 focus:ring-red-500"
                  />
                  <div>
                    <span className="font-semibold text-red-800">Schedule II Verification</span>
                    <p className="text-sm text-red-700 mt-1">
                      I verify that a double count has been completed by two qualified personnel.
                    </p>
                  </div>
                </label>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Inventory Selection (FEFO) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Available Inventory (FEFO - First Expiry First Out)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {lotsLoading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : sortedLots.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Lot #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Expiration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Available</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Unit Cost</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">DSCSA</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {sortedLots.map((lot: any, index: number) => (
                    <tr 
                      key={lot.id}
                      className={fillData.lotNumber === lot.lotNumber ? 'bg-blue-50' : 'hover:bg-slate-50'}
                    >
                      <td className="px-4 py-3 font-mono text-sm">{lot.lotNumber}</td>
                      <td className="px-4 py-3">
                        <span className={index === 0 ? 'text-amber-600 font-medium' : ''}>
                          {format(new Date(lot.expirationDate), 'MM/dd/yyyy')}
                        </span>
                        {index === 0 && (
                          <Badge variant="warning" className="ml-2">First Expiry</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">{lot.quantityAvailable}</td>
                      <td className="px-4 py-3">{formatCurrency(lot.unitCostCents)}</td>
                      <td className="px-4 py-3">{lot.binLocation}</td>
                      <td className="px-4 py-3">
                        {lot.dscsaVerified ? (
                          <span className="text-emerald-600 flex items-center gap-1">
                            <CheckCircle2 className="h-4 w-4" />
                            Verified
                          </span>
                        ) : (
                          <span className="text-slate-400">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant={fillData.lotNumber === lot.lotNumber ? 'primary' : 'outline'}
                          onClick={() => handleSelectLot(lot)}
                          disabled={lot.quantityAvailable < rx.quantity}
                        >
                          {fillData.lotNumber === lot.lotNumber ? 'Selected' : 'Select'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <Alert variant="warning">
              No inventory available for this NDC. Contact purchasing.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between items-center pt-6 border-t">
        <Button
          variant="outline"
          onClick={() => printLabelMutation.mutate()}
          loading={printLabelMutation.isPending}
        >
          <Printer className="h-4 w-4 mr-2" />
          Print Label
        </Button>
        <Button
          disabled={!canFill}
          onClick={() => fillMutation.mutate()}
          loading={fillMutation.isPending}
        >
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Complete Fill
        </Button>
      </div>
    </div>
  );
}
